/**
 * Atlas PM · 新建项目 · 「自动创建任务」picker（P8.29：选模板 + 当场设周期）
 *
 * 数据源：
 *   · GET /api/task-templates → 启用中的任务模板（默认只返回 isActive=true）
 *
 * 交互（不再有「未配置自动创建规则」灰字 —— 任何启用的模板都可选）：
 *   · 每行：[任务模板 ▼] [周期 ▼] [月份 ▼?] [周期日 ▼] [×]
 *   · 周期：每周 / 每月 / 每季 / 每年
 *     周期日：每周 → 周一..周日，其余 → 1-28号 +「最后一天」
 *     月份：每季 → 第1/2/3月；每年 → 1月..12月；每周/每月 → 无
 *   · 默认：周期「每月」、周期日 5；模板为空
 *   · 同一模板不能在多行重复选（选中后从其他行的模板下拉里排除）
 *   · 提交：autoTasks = [{ templateId, cycleType, cycleMonth, cycleDay }]；空行（没选模板）自动丢弃
 *   · 仅在 create 模式使用；edit 模式走 ProjectAutoTaskPanel。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { AutoCycleType, AutoTaskInput } from './autoTaskApi';
import {
  CYCLE_TYPE_LABEL,
  clampCycleDay,
  clampCycleMonth,
  cycleDayOptions,
  cycleMonthOptions,
  cyclePreviewText,
} from './autoTaskApi';
import { useTaskTemplates } from './useAutoTask';

export interface ProjectAutoTaskPickerProps {
  /** 已选条目（仅含已填模板的行）；外部回填时据此重建行 */
  value: AutoTaskInput[];
  onChange(items: AutoTaskInput[]): void;
}

interface Row {
  key: number;
  templateId: string;
  cycleType: AutoCycleType;
  cycleMonth: number | null;
  cycleDay: number;
}

let rowSeq = 0;
const nextKey = () => ++rowSeq;

const emptyRow = (): Row => ({
  key: nextKey(),
  templateId: '',
  cycleType: 'MONTHLY',
  cycleMonth: null,
  cycleDay: 5,
});

export default function ProjectAutoTaskPicker({
  value,
  onChange,
}: ProjectAutoTaskPickerProps) {
  const templatesQ = useTaskTemplates();
  const [rows, setRows] = useState<Row[]>(() =>
    value.length > 0
      ? value.map((v) => ({
          key: nextKey(),
          templateId: String(v.templateId),
          cycleType: v.cycleType,
          cycleMonth: v.cycleMonth ?? null,
          cycleDay: v.cycleDay,
        }))
      : [emptyRow()],
  );

  const templates = templatesQ.data ?? [];

  // ⭐ 受控契约（P8.32 修正）：外部 value 变化（弹窗复用 / 数据回填）时据此重建行。
  //    - 判据是【已选模板的行】与父值的签名是否一致；父值非空且不一致才回填——
  //      会话内每次 emit 后两者必然一致（emit 的 value 就是由本地行推导而来），
  //      因此正常编辑绝不会触发重建（H1/H3 不可能）。
  //    - ⭐ 未选模板的空行【绝不由父值重建】：父层只会收到已选模板行，空行的
  //      「周期 / 月份 / 周期日」只存在于本地；重建时必须原样保留，否则会丢输入。
  const rowSig = (r: { templateKey: string; cycleType: AutoCycleType; cycleMonth: number | null; cycleDay: number }) =>
    `${r.templateKey}:${r.cycleType}:${r.cycleMonth ?? ''}:${r.cycleDay}`;
  const localTplSig = rows
    .filter((r) => r.templateId !== '')
    .map((r) => rowSig({ templateKey: r.templateId, cycleType: r.cycleType, cycleMonth: r.cycleMonth, cycleDay: r.cycleDay }))
    .join('|');
  const valueSig = value
    .map((v) => rowSig({ templateKey: String(v.templateId), cycleType: v.cycleType, cycleMonth: v.cycleMonth ?? null, cycleDay: v.cycleDay }))
    .join('|');
  const initedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current && value.length > 0 && localTplSig !== valueSig) {
      setRows((cur) => {
        // 父值按 templateId 映射，重建时尽量沿用既有行的 key（DOM 不重挂）
        const valByTpl = new Map(value.map((v) => [String(v.templateId), v] as const));
        const next: Row[] = [];
        for (const r of cur) {
          if (r.templateId === '') {
            // ⭐ 空行连同其周期设置原样保留，不被回填清空
            next.push(r);
            continue;
          }
          const vv = valByTpl.get(r.templateId);
          if (vv) {
            next.push({
              key: r.key,
              templateId: r.templateId,
              cycleType: vv.cycleType,
              cycleMonth: vv.cycleMonth ?? null,
              cycleDay: vv.cycleDay,
            });
            valByTpl.delete(r.templateId);
          }
          // 本地有、父值没有的模板行：以父值为准（数据回填语义），丢弃
        }
        for (const [tid, vv] of valByTpl) {
          next.push({
            key: nextKey(),
            templateId: tid,
            cycleType: vv.cycleType,
            cycleMonth: vv.cycleMonth ?? null,
            cycleDay: vv.cycleDay,
          });
        }
        return next;
      });
    }
    initedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /** 由行推导上报的 AutoTaskInput[]：丢弃未选模板的空行 */
  const emit = (nextRows: Row[]) => {
    setRows(nextRows);
    onChange(
      nextRows
        .filter((r) => r.templateId !== '')
        .map((r) => {
          const out: AutoTaskInput = {
            templateId: Number(r.templateId),
            cycleType: r.cycleType,
            cycleMonth: r.cycleMonth,
            cycleDay: r.cycleDay,
          };
          // WEEKLY / MONTHLY 后端要求 cycleMonth 为空（null 可被 POST DTO 接受）
          if (r.cycleType === 'WEEKLY' || r.cycleType === 'MONTHLY') {
            out.cycleMonth = null;
          }
          return out;
        }),
    );
  };

  const patchRow = (index: number, patch: Partial<Row>) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    emit(next);
  };

  const onTemplateChange = (index: number, templateId: string) => patchRow(index, { templateId });

  const onCycleTypeChange = (index: number, cycleType: AutoCycleType) => {
    const row = rows[index];
    if (!row) return;
    const day = clampCycleDay(cycleType, row.cycleDay);
    const month =
      cycleType === 'WEEKLY' || cycleType === 'MONTHLY'
        ? null
        : clampCycleMonth(cycleType, row.cycleMonth);
    patchRow(index, { cycleType, cycleDay: day, cycleMonth: month });
  };

  const onCycleMonthChange = (index: number, cycleMonth: number) =>
    patchRow(index, { cycleMonth });

  const onCycleDayChange = (index: number, cycleDay: number) => patchRow(index, { cycleDay });

  const addRow = () => {
    if (rows.length > 0 && rows.at(-1)?.templateId === '') return;
    setRows((cur) => [...cur, emptyRow()]);
  };

  const removeRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    emit(next.length ? next : [emptyRow()]);
  };

  // 已选模板集合（用于从其他行的下拉中排除，避免同一模板重复选）
  const takenIds = useMemo(
    () => new Set(rows.map((r) => r.templateId).filter(Boolean)),
    [rows],
  );

  const isLoading = templatesQ.isLoading;

  return (
    <div className="pta-picker" data-testid="pta-picker">
      {isLoading && <div className="pm-field__hint">加载任务模板…</div>}
      {!isLoading && templates.length === 0 && (
        <div className="pm-field__hint" data-testid="pta-empty">
          暂无可用的任务模板
        </div>
      )}
      {!isLoading && templates.length > 0 && rows.map((row, i) => (
        <div className="pta-pick" key={row.key}>
          <div className="pta-pick__field pta-pick__field--grow">
            <span className="pta-pick__label">任务模板</span>
            <select
              className="pm-select"
              data-testid="pta-template-select"
              value={row.templateId}
              onChange={(e) => onTemplateChange(i, e.target.value)}
            >
              <option value="">请选择任务模板</option>
              {templates.map((t) => {
                const taken = row.templateId !== String(t.id) && takenIds.has(String(t.id));
                return (
                  <option key={t.id} value={String(t.id)} disabled={taken}>
                    {t.name}
                    {taken ? '（已选择）' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="pta-pick__field">
            <span className="pta-pick__label">周期</span>
            <select
              className="pm-select"
              data-testid="pta-cycle-type-select"
              value={row.cycleType}
              onChange={(e) => onCycleTypeChange(i, e.target.value as AutoCycleType)}
            >
              {(Object.keys(CYCLE_TYPE_LABEL) as AutoCycleType[]).map((ct) => (
                <option key={ct} value={ct}>
                  {CYCLE_TYPE_LABEL[ct]}
                </option>
              ))}
            </select>
          </div>
          {cycleMonthOptions(row.cycleType).length > 0 && (
            <div className="pta-pick__field" data-testid="pta-cycle-month-field">
              <span className="pta-pick__label">月份</span>
              <select
                className="pm-select"
                data-testid="pta-cycle-month-select"
                value={row.cycleMonth ?? clampCycleMonth(row.cycleType, null) ?? 1}
                onChange={(e) => onCycleMonthChange(i, Number(e.target.value))}
              >
                {cycleMonthOptions(row.cycleType).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="pta-pick__field">
            <span className="pta-pick__label">周期日</span>
            <select
              className="pm-select"
              data-testid="pta-cycle-day-select"
              value={row.cycleDay}
              onChange={(e) => onCycleDayChange(i, Number(e.target.value))}
            >
              {cycleDayOptions(row.cycleType).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="pm-linkbtn is-danger pta-pick__remove"
            aria-label="移除该行"
            onClick={() => removeRow(i)}
          >
            ×
          </button>
          {row.templateId !== '' && (
            <div className="pm-field__hint pta-preview" data-testid="pta-preview">
              {cyclePreviewText(row.cycleType, row.cycleMonth, row.cycleDay)}
            </div>
          )}
        </div>
      ))}

      <button type="button" className="pm-linkbtn pta-pick__add" data-testid="pta-add-row" onClick={addRow}>
        ＋ 添加一条
      </button>

      <div className="pm-field__hint" data-testid="pta-picker-hint">
        创建项目时立即按所选周期生成当前周期的任务；同一模板不可重复选择。
      </div>
    </div>
  );
}